/* ============================================================
   ARCHIVE 135 - cart drawer

   Intercepts add-to-cart submits, posts to /cart/add.js, then re-renders the
   drawer from the Section Rendering API.

   There is deliberately NO form.submit() anywhere in this file. Re-submitting
   after a failure cannot be done safely: when a fetch rejects (network drop,
   reset, abort) the POST may already have committed server-side with only the
   response lost, so a retry would silently double the line. The rule is: only
   ever navigate, never re-POST.

   Failure policy:
   - Non-2xx from /cart/add.js: show Shopify's own reason in the page and stay
     put. Note a 422 can be a PARTIAL success (verified live: "Only 50 items
     were added to your cart due to availability."), so the cart is re-synced
     on this path too, otherwise the count would silently disagree with the
     real cart.
   - It committed but our re-render broke, OR we never got a response at all
     (including a timed-out request, which aborts): send the customer to /cart,
     the one place that always tells the truth.
   - No JS: nothing is intercepted and the form posts natively. That is the
     real fallback.

   Every listener is attached through an init() that is safe to run again, so a
   shopify:section:load in the theme editor does not leave the drawer, its
   REMOVE links or the header's CART trigger dead.
   ============================================================ */

(function () {
  'use strict';

  const SECTION_ID = 'cart-drawer';
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const GENERIC_ERROR = 'Something went wrong adding that to your cart. Please try again.';
  const FETCH_TIMEOUT = 10000;
  const OVERLAY_SELECTOR = '.cartd.is-open, .mobile-menu.is-open, .product-lightbox[open], .film-lightbox[open]';

  let lastFocused = null;
  let busy = false;
  let hideTimer = null;
  let refreshVersion = 0;
  // Cached once per drawer instance. The live region now sits OUTSIDE
  // [data-cart-drawer-content], the node that every refresh replaces, so this
  // reference stays valid; when it lived inside, each render threw away the
  // element an announcement had just been written to.
  let liveRegion = null;

  const boundFeatures = new WeakMap();

  function claim(el, feature) {
    if (!el) return false;
    let features = boundFeatures.get(el);
    if (!features) {
      features = {};
      boundFeatures.set(el, features);
    }
    if (features[feature]) return false;
    features[feature] = true;
    return true;
  }

  // Not rendered on /cart, so every entry point below is a no-op there and
  // native posts take over.
  function getDrawer() {
    return document.querySelector('[data-cart-drawer]');
  }

  function getPanel() {
    const drawer = getDrawer();
    return drawer ? drawer.querySelector('[data-cart-panel]') : null;
  }

  function announce(message) {
    if (liveRegion) liveRegion.textContent = message;
  }

  // A hung request would otherwise leave the button stuck on ADDING forever.
  // AbortSignal.timeout is missing on older Safari, where undefined simply
  // means "no timeout", as before.
  function timeoutSignal() {
    if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') return undefined;
    return AbortSignal.timeout(FETCH_TIMEOUT);
  }

  /* ---------- overlay chrome ----------
     Mirrors the helpers in assets/theme.js so the mobile menu and this drawer
     share one mechanism. State is DERIVED from the DOM rather than counted, so
     closing one overlay while the other is still open cannot unlock the page.

     base.css sets html,body{overflow-x:clip}. Per CSS Overflow, body overflow
     only propagates to the viewport while html's overflow is visible, so the
     usual document.body.style.overflow='hidden' is a NO-OP on this theme and
     the page would scroll behind the open drawer. Lock the root element. */

  function syncOverlayChrome() {
    const overlays = Array.prototype.slice.call(document.querySelectorAll(OVERLAY_SELECTOR));
    document.documentElement.classList.toggle('cartd-locked', overlays.length > 0);

    // Everything outside the open overlay is inert, so Tab and the screen
    // reader's virtual cursor cannot walk into the page behind it. Never inert
    // the branch that holds an overlay.
    Array.prototype.forEach.call(document.body.children, function (child) {
      const holdsOverlay = overlays.some(function (overlay) {
        return child.contains(overlay);
      });
      if (overlays.length > 0 && !holdsOverlay) {
        child.setAttribute('inert', '');
      } else {
        child.removeAttribute('inert');
      }
    });
  }

  /* ---------- open / close ---------- */

  function focusable() {
    const panel = getPanel();
    if (!panel) return [];
    return Array.prototype.slice.call(
      panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter(function (el) { return el.offsetParent !== null; });
  }

  function onKeydown(event) {
    if (event.key === 'Escape') {
      close();
      return;
    }
    if (event.key !== 'Tab') return;
    const panel = getPanel();
    if (!panel) return;
    const items = focusable();
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    // Pull focus back if it has escaped the panel entirely.
    if (!panel.contains(document.activeElement)) {
      event.preventDefault();
      first.focus();
      return;
    }
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // returnFocusTo is passed explicitly, because by the time the drawer opens the
  // add-to-cart button has been disabled and is no longer document.activeElement
  // (focus has already fallen back to <body>).
  function open(returnFocusTo) {
    const drawer = getDrawer();
    if (!drawer) return;
    if (hideTimer) {
      window.clearTimeout(hideTimer);
      hideTimer = null;
    }
    lastFocused = returnFocusTo || document.activeElement;
    drawer.hidden = false;
    // Force a frame so the transform transition runs from its start value.
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      // Locking and inerting read .is-open back off the DOM, so they belong
      // here rather than before the class lands.
      syncOverlayChrome();
      const first = focusable()[0];
      if (first) first.focus();
    });
    drawer.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', onKeydown);
  }

  function close() {
    const drawer = getDrawer();
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.removeEventListener('keydown', onKeydown);
    // Must run BEFORE the focus restore: focus() is a no-op on an inert node.
    syncOverlayChrome();
    if (hideTimer) window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(function () {
      hideTimer = null;
      if (!drawer.classList.contains('is-open')) drawer.hidden = true;
    }, 320);
    if (lastFocused && document.contains(lastFocused) && lastFocused.offsetParent !== null) {
      lastFocused.focus();
    }
  }

  /* ---------- render ---------- */

  function syncCartCount(count) {
    // Visible text is the accessible name here (WCAG 2.5.3 Label in Name), so
    // do not paint an aria-label over it.
    document.querySelectorAll('[data-cart-count-text]').forEach(function (el) {
      el.textContent = 'CART (' + count + ')';
    });
  }

  async function refresh(beforeReplace) {
    const drawer = getDrawer();
    if (!drawer) throw new Error('no drawer');
    const version = ++refreshVersion;
    const response = await fetch(ROOT + '?sections=' + SECTION_ID, {
      headers: { Accept: 'application/json' },
      signal: timeoutSignal()
    });
    if (!response.ok) throw new Error('section render failed');
    const payload = await response.json();
    const markup = payload[SECTION_ID];
    if (!markup) throw new Error('section missing');

    const parsed = new DOMParser().parseFromString(markup, 'text/html');
    const next = parsed.querySelector('[data-cart-drawer-content]');
    const current = drawer.querySelector('[data-cart-drawer-content]');
    if (!next || !current) throw new Error('content missing');
    // A header-triggered read started before a quantity change must not replace
    // the authoritative response from that change with an older cart snapshot.
    if (version !== refreshVersion) return current.getAttribute('data-cart-count') || '0';

    if (beforeReplace) beforeReplace();
    current.innerHTML = next.innerHTML;
    const count = next.getAttribute('data-cart-count') || '0';
    current.setAttribute('data-cart-count', count);
    syncCartCount(count);
    return count;
  }

  // Best-effort re-sync used on the error path and behind an already-open
  // drawer, where failing to re-render must not itself become an error.
  async function refreshQuietly() {
    try {
      await refresh();
    } catch (_error) {
      /* leave the UI as it is; /cart remains correct */
    }
  }

  /* ---------- inline form errors ---------- */

  function showFormError(form, message) {
    let note = form.querySelector('[data-cart-error]');
    if (!note) {
      note = document.createElement('p');
      note.className = 'cart-error';
      note.setAttribute('data-cart-error', '');
      note.setAttribute('role', 'alert');
      form.appendChild(note);
    }
    note.textContent = message;
  }

  function clearFormError(form) {
    const note = form.querySelector('[data-cart-error]');
    if (note) note.remove();
  }

  // Shopify answers non-2xx with {message, description}. Verified live:
  // {"status":422,"message":"Cart Error","description":"Cannot find variant"} and
  // {"status":422,"message":"Only 50 items were added to your cart due to availability.", ...}
  async function readShopifyError(response) {
    try {
      const data = await response.json();
      const text = data && (data.description || data.message);
      if (typeof text !== 'string' || !text.trim()) return null;
      // description can carry basic HTML; take the text only. Parsed in an
      // inert document rather than through innerHTML on a detached node, which
      // still runs <img onerror> in Chromium.
      const parsed = new DOMParser().parseFromString(text, 'text/html');
      return (parsed.body.textContent || '').trim() || null;
    } catch (_error) {
      return null;
    }
  }

  /* ---------- add to cart ---------- */

  async function onSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (busy && form.closest('[data-cart-drawer]')) {
      event.preventDefault();
      return;
    }
    const action = form.getAttribute('action') || '';
    if (action.indexOf('/cart/add') === -1) return;
    if (!getDrawer()) return;
    if (busy) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    busy = true;
    refreshVersion++;
    clearFormError(form);
    clearDrawerError();

    const button = form.querySelector('[name="add"]');
    // Capture the focus target BEFORE disabling the button, or focus has already
    // fallen back to <body> and closing the drawer strands the customer at the
    // top of the document.
    const returnFocusTo = event.submitter || button || document.activeElement;
    const originalLabel = button ? button.textContent : '';
    if (button) {
      button.disabled = true;
      button.textContent = 'ADDING...';
    }

    function restoreButton() {
      busy = false;
      if (button) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }

    let responded = false;
    let committed = false;

    try {
      const response = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: new FormData(form),
        signal: timeoutSignal()
      });
      responded = true;

      if (!response.ok) {
        const message = await readShopifyError(response);
        restoreButton();
        showFormError(form, message || GENERIC_ERROR);
        // A 422 can be a PARTIAL add, so the cart may well have changed. Re-sync
        // the count and drawer rather than leaving them contradicting the cart.
        // Deliberately not opening the drawer: the message in the form is the
        // primary feedback and the drawer would cover it.
        await refreshQuietly();
        return;
      }

      committed = true;
      await refresh();
      open(returnFocusTo);
      announce('Added to cart.');
    } catch (_error) {
      if (committed || !responded) {
        // Either it IS in the cart and only our re-render broke, or we never got
        // a response (a timeout aborts into here too) and cannot know. Never
        // re-POST. Show the real cart.
        window.location.href = ROOT + 'cart';
        return;
      }
      restoreButton();
      showFormError(form, GENERIC_ERROR);
      return;
    }

    restoreButton();
  }

  /* ---------- quantity and remove controls inside the drawer ---------- */

  function clearDrawerError() {
    const note = getDrawer() && getDrawer().querySelector('[data-cart-drawer-error]');
    if (!note) return;
    note.hidden = true;
    note.textContent = '';
  }

  function showDrawerError(message) {
    const note = getDrawer() && getDrawer().querySelector('[data-cart-drawer-error]');
    if (!note) return;
    note.hidden = false;
    note.textContent = message;
  }

  function findLine(key) {
    const drawer = getDrawer();
    return drawer && Array.prototype.find.call(drawer.querySelectorAll('[data-cart-line-key]'), function (line) {
      return line.getAttribute('data-cart-line-key') === key;
    });
  }

  function captureDrawerFocus() {
    const active = document.activeElement;
    const panel = getPanel();
    if (!panel || !active || !panel.contains(active)) return null;
    const line = active.closest('[data-cart-line-key]');
    const key = line && line.getAttribute('data-cart-line-key');
    if (active.matches('[data-cart-close]')) return { selector: '[data-cart-close]' };
    if (active.matches('.cartd__view')) return { selector: '.cartd__view' };
    if (active.matches('[name="checkout"]')) return { selector: '[name="checkout"]' };
    if (active.matches('[data-cart-remove]')) return { key: key, selector: '[data-cart-remove]' };
    if (active.matches('[data-cart-step]')) return { key: key, selector: '[data-cart-step="' + active.getAttribute('data-cart-step') + '"]:not([disabled])' };
    return null;
  }

  function restoreLineFocus(key, step, focusChoice) {
    const drawer = getDrawer();
    if (!drawer || !drawer.classList.contains('is-open')) return;
    const line = findLine(key);
    const preferred = line && line.querySelector('[data-cart-step="' + step + '"]:not([disabled])');
    const fallback = line && line.querySelector('[data-cart-step]:not([disabled])');
    const focusScope = focusChoice && (focusChoice.key ? findLine(focusChoice.key) : drawer);
    const chosen = focusScope && focusScope.querySelector(focusChoice.selector);
    const target = chosen || preferred || fallback || focusable()[0];
    if (target) target.focus();
  }

  async function changeLine(key, quantity, title, step) {
    if (busy || !key || !Number.isSafeInteger(quantity) || quantity < 0) return;
    busy = true;
    refreshVersion++;
    clearDrawerError();
    announce('Updating cart.');
    const panel = getPanel();
    const disabledStates = Array.prototype.map.call(panel.querySelectorAll('[data-cart-step], [name="checkout"]'), function (button) {
      const wasDisabled = button.disabled;
      button.disabled = true;
      return { button: button, disabled: wasDisabled };
    });
    panel.setAttribute('aria-busy', 'true');
    let focusChoice = null;

    try {
      // Absolute quantities and stable line keys avoid increment replay and
      // positional line changes. Never retry a POST whose response was lost.
      const response = await fetch(ROOT + 'cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: quantity }),
        signal: timeoutSignal()
      });
      const message = response.ok ? null : await readShopifyError(response);
      // Preserve any control the customer reached while the request was in
      // flight; the section swap is about to replace that focused element.
      const count = await refresh(function () { focusChoice = captureDrawerFocus(); });
      if (!response.ok) {
        announce('');
        showDrawerError(message || 'We could not update this quantity. Please try again.');
      } else if (quantity === 0) {
        announce(count === '0' ? 'Cart is empty.' : 'Item removed.');
      } else {
        const line = findLine(key);
        const value = line && line.querySelector('[data-cart-quantity-value]');
        announce(title + ' quantity updated' + (value ? ' to ' + value.textContent.trim() : '') + '.');
      }
    } catch (_error) {
      // The server may have committed; show the authoritative cart without
      // replaying the update, including when only section rendering failed.
      window.location.href = ROOT + 'cart';
    } finally {
      busy = false;
      panel.removeAttribute('aria-busy');
      disabledStates.forEach(function (state) {
        if (state.button.isConnected) state.button.disabled = state.disabled;
      });
      restoreLineFocus(key, step, focusChoice);
    }
  }

  function onQuantitySubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-cart-quantity-form]')) return;
    event.preventDefault();
    const button = event.submitter;
    if (!button || button.name !== 'quantity') return;
    changeLine(form.getAttribute('data-cart-key'), Number(button.value), form.getAttribute('data-cart-title') || 'Item', button.getAttribute('data-cart-step'));
  }

  function onRemoveClick(event) {
    const remove = event.target.closest('[data-cart-remove]');
    if (!remove) return;
    event.preventDefault();
    changeLine(remove.getAttribute('data-cart-key'), 0, 'Item', 'minus');
  }

  /* ---------- init (re-runnable) ---------- */

  function initDrawer() {
    const drawer = getDrawer();
    if (!drawer) return;
    liveRegion = drawer.querySelector('[data-cart-live]');
    if (!claim(drawer, 'drawer')) return;

    drawer.addEventListener('click', function (event) {
      if (event.target.closest('[data-cart-close]')) {
        event.preventDefault();
        close();
      }
    });
    drawer.addEventListener('click', onRemoveClick);
  }

  function initTriggers() {
    document.querySelectorAll('[data-cart-open]').forEach(function (trigger) {
      if (!claim(trigger, 'cart-open')) return;
      trigger.addEventListener('click', function (event) {
        // Let modified clicks (new tab, download, save) behave as normal links.
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        if (event.button !== 0) return;
        if (!getDrawer()) return;
        event.preventDefault();
        // Open on the markup the server already rendered instead of waiting on
        // a round-trip: the drawer used to sit dead for the length of a
        // /?sections= request, and stayed dead entirely while an add was in
        // flight because the busy check ran after preventDefault.
        open(trigger);
        // Reconcile behind the open drawer. Skipped while an add is running,
        // whose own refresh() is already authoritative and would otherwise race
        // this one. On failure the drawer simply keeps the content it has.
        if (!busy) refreshQuietly();
      });
    });
  }

  function initAll() {
    initDrawer();
    initTriggers();
  }

  document.addEventListener('submit', onQuantitySubmit);
  document.addEventListener('submit', onSubmit);
  initAll();

  // The theme editor replaces one section's markup at a time, which would
  // otherwise leave the drawer and the header's CART link with no listeners.
  document.addEventListener('shopify:section:load', initAll);
})();
